import { useState, useEffect, useRef } from "react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";

// Import the separate broker components
import { FyersUserForm } from "./FyersUserForm";
import { NubraUserForm } from "./NubraUserForm";
import { DhanSSXTSUserForm } from "./DhanSSXTSUserForm";
import { DhanHQUserForm } from "./DhanHQUserForm";

export function AddNewUserModal({ handleAddFyersUserIndexMuliplier, handleAddNubraUserIndexMuliplier, handleAddDhanXTSUserIndexMultiplier, handleAddDhanHQUserIndexMultiplier }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("fyers");
  const [proxyData, setProxyData] = useState(null);
  const [loadingProxy, setLoadingProxy] = useState(false);
  const proxyDataRef = useRef(null);

  useEffect(() => {
    const claimProxy = async () => {
      if (proxyDataRef.current || loadingProxy) return;
      setLoadingProxy(true);
      try {
        const response = await apiClient.request({
          method: "get",
          url: `${server.godseye.baseUrl}proxies/next`
        });
        if (response.data) {
          setProxyData(response.data);
          proxyDataRef.current = response.data;
        }
      } catch (error) {
        console.error("Failed to claim proxy:", error);
        toast.error("Another user is currently adding a user or no proxies available.");
        setOpen(false);
      } finally {
        setLoadingProxy(false);
      }
    };

    const releaseProxy = async () => {
      const currentProxy = proxyDataRef.current;
      if (currentProxy) {
        try {
          await apiClient.request({
            method: "post",
            url: `${server.godseye.baseUrl}proxies/release`,
            data: { proxy_static_ip: currentProxy.proxy_static_ip }
          });
        } catch (error) {
          console.error("Failed to release proxy:", error);
        }
        setProxyData(null);
        proxyDataRef.current = null;
      }
    };

    if (open) {
      if (activeTab !== "dhan_xts") {
        claimProxy();
      } else {
        releaseProxy();
      }
    } else {
      releaseProxy();
      setActiveTab("fyers");
    }
  }, [open, activeTab]);

  useEffect(() => {
    if (!proxyData) return;
    const heartbeatInterval = setInterval(async () => {
      try {
        await apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/heartbeat`,
          data: { proxy_static_ip: proxyData.proxy_static_ip }
        });
      } catch (err) {
        console.error("Heartbeat failed", err);
      }
    }, 30000);

    return () => clearInterval(heartbeatInterval);
  }, [proxyData]);

  useEffect(() => {
    return () => {
      if (proxyDataRef.current) {
        apiClient.request({
          method: "post",
          url: `${server.godseye.baseUrl}proxies/release`,
          data: { proxy_static_ip: proxyDataRef.current.proxy_static_ip }
        }).catch(err => console.error(err));
      }
    };
  }, []);

  const handleClose = async (isSuccess = false) => {
    if (isSuccess && proxyData) {
      try {
        await apiClient.request({
            method: "post",
            url: `${server.godseye.baseUrl}proxies/commit`,
            data: { proxy_static_ip: proxyData.proxy_static_ip }
        });
        setProxyData(null); // Clear so releaseProxy ignores it
        proxyDataRef.current = null;
      } catch (error) {
        console.error("Failed to commit proxy:", error);
      }
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="text-[16px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
        >
          Add New User
        </Button>
      </DialogTrigger>
      <DialogContent className="font-inter sm:max-w-[1025px] border-zinc-700">
        <DialogHeader className="sr-only">
          <DialogTitle className="text-white">Add New User</DialogTitle>
          <DialogDescription>
            Fill in the details to add a new user.
          </DialogDescription>
        </DialogHeader>

        {loadingProxy ? (
          <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="w-10 h-10 border-4 border-zinc-600 border-t-zinc-200 rounded-full animate-spin mb-4"></div>
            <p className="text-zinc-300 font-medium">Assigning Proxy...</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex items-center w-[500px] mx-auto ">
              <TabsTrigger value="fyers" className="w-full">
                Fyers
              </TabsTrigger>
              <TabsTrigger value="nubra" className="w-full">
                Nubra
              </TabsTrigger>
              <TabsTrigger value="dhan_xts" className="w-full">
                Dhan XTS
              </TabsTrigger>
              <TabsTrigger value="dhan_hq" className="w-full">
                Dhan HQ
              </TabsTrigger>
            </TabsList>

            <TabsContent value="fyers">
              <FyersUserForm
                handleAddFyersUserIndexMuliplier={handleAddFyersUserIndexMuliplier}
                onClose={handleClose}
                proxyData={proxyData}
              />
            </TabsContent>

            <TabsContent value="nubra">
              <NubraUserForm 
                handleAddNubraUserIndexMuliplier={handleAddNubraUserIndexMuliplier} 
                onClose={handleClose} 
                proxyData={proxyData}
              />
            </TabsContent>

            <TabsContent value="dhan_xts">
              <DhanSSXTSUserForm 
                handleAddUserIndexMultiplier={handleAddDhanXTSUserIndexMultiplier} 
                onClose={handleClose} 
                proxyData={proxyData}
              />
            </TabsContent>

            <TabsContent value="dhan_hq">
              <DhanHQUserForm 
                handleAddDhanHQUserIndexMultiplier={handleAddDhanHQUserIndexMultiplier} 
                onClose={handleClose} 
                proxyData={proxyData}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}


































































// import { useState } from "react";
// import { Button } from "../../../components/ui/button";
// import {
//   Dialog,
//   DialogContent,
//   DialogDescription,
//   DialogFooter,
//   DialogHeader,
//   DialogTitle,
//   DialogTrigger,
// } from "../../../components/ui/dialog";

// import {
//   Select,
//   SelectTrigger,
//   SelectValue,
//   SelectContent,
//   SelectGroup,
//   SelectItem,
// } from "../../../components/ui/select";

// import { Label } from "../../../components/ui/label";

// import apiClient from "../../../utils/apiClient";
// import { Input } from "../../../components/ui/input";
// import toast from "react-hot-toast";

// const statusOptions = [
//   { value: "active", label: "active" },
//   { value: "inactive", label: "inactive" },
//   { value: "deactivated", label: "deactivated" },
//   { value: "dummy", label: "dummy" },
// ];

// export function AddNewUserModal({ handleAddFyersUserIndexMuliplier }) {
//   // 0 ---> b , 1 --> g , -1 --> r

//   const [open, setOpen] = useState(false);
//   const [clientId, setClientId] = useState("");
//   const [name, setName] = useState("");
//   const [totpKey, setTotpKey] = useState("");
//   const [pin, setPin] = useState("");
//   const [appId, setAppId] = useState("");
//   const [appType, setAppType] = useState("");
//   const [appSecret, setAppSecret] = useState("");
//   const [redirectUrl, setRedirectUrl] = useState("");
//   const [appIdType, setAppIdType] = useState("");
//   const [status, setStatus] = useState("");
//   const [phoneNumber, setPhoneNumber] = useState("");
//   const [activationUrl, setActivationUrl] = useState("");
//   const [multiplier, setMultiplier] = useState(1);
//   const [userData, setUserData] = useState(null);

//   const handleAddNewUser = () => {
//     const data = {
//       client_id: clientId,
//       name: name,
//       totp_key: totpKey,
//       pin: pin,
//       app_id: appId,
//       app_type: appType,
//       app_secret: appSecret,
//       redirect_url: redirectUrl,
//       app_id_type: appIdType,
//       status: status,
//       phone_number: phoneNumber,
//     };

//     if (
//       !data.client_id ||
//       !data.name ||
//       !data.totp_key ||
//       !data.pin ||
//       !data.app_id ||
//       !data.app_type ||
//       !data.app_secret ||
//       !data.redirect_url ||
//       !data.app_id_type ||
//       !data.status ||
//       !data.phone_number ||
//       !multiplier
//     ) {
//       console.log("Missing required fields:", data);
//       toast.error("Missing required fields !!", {
//             duration: 5000,
//       });
//       return;
//     }
//     console.log("New User Data", data);
//     console.log("New User  multiplier", multiplier);

//     // Store the user data
//     setUserData(data);

//     let config = {
//       method: "post",
//       url: "https://godseye.server.sypto.xyz/add-fyers-user",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       data: JSON.stringify(data), // Convert the data object to a JSON string
//     };

//     axios
//       .request(config)
//       .then((response) => {
//         console.log("Server response:", response.data);
//         // Optionally handle the response data here
//         if (response.data.status === 200) {
//           toast.success("New user added successfully!", {
//             duration: 5000,
//           });
//           console.log("New user added successfully!");
//           handleAddFyersUserIndexMuliplier(clientId, name, multiplier);
//         }
//       })
//       .catch((error) => {
//         console.error("Failed to add new user", error);
//       })
//       .finally(() => {
//         setOpen(false); // Close the modal or perform any final actions
//       });
//   };

//   const handleActivateUser = () => {
//     const data = {
//       app_id: appId,
//       app_type: appType,
//       app_secret: appSecret,
//       redirect_url: redirectUrl,
//     };

//     if (
//       !data.app_id ||
//       !data.app_type ||
//       !data.app_secret ||
//       !data.redirect_url
//     ) {
//       console.log("Missing required fields for activation:", data);
//       toast.error("Please fill in all required fields for activation.");
//       return;
//     }

//     console.log("User Data for Account Activation ", data);

//     let mainConfig = {
//       method: "POST",
//       url: "https://godseye.server.sypto.xyz/activate-user",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       data: JSON.stringify(data),
//     };

//     axios
//       .request(mainConfig)
//       .then((response) => {
//         console.log("Main API activation response:", response.data);

//         if (response.data && response.data.url) {
//           setActivationUrl(response.data.url);
//         }
//       })
//       .catch((error) => {
//         console.error("Failed to activate user with main API", error);
//         setActivationUrl(`Error: ${error.message}`);
//       });
//   };

//   return (
//     <Dialog open={open} onOpenChange={setOpen}>
//       <DialogTrigger asChild>
//         <Button
//           variant="outline"
//           className="text-[16px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
//         >
//           Add New User
//         </Button>
//       </DialogTrigger>
//       <DialogContent className="sm:max-w-[1025px] border-zinc-700">
//         <DialogHeader>
//           <DialogTitle className="text-white">Add new User Details</DialogTitle>
//         </DialogHeader>

//         <div className="grid grid-cols-3 gap-4 py-4">
//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Client Id</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={clientId}
//               onChange={(e) => setClientId(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Name</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">TOTP Key</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={totpKey}
//               onChange={(e) => setTotpKey(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Pin</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={pin}
//               onChange={(e) => setPin(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">App Id</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={appId}
//               onChange={(e) => setAppId(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">App Type</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={appType}
//               onChange={(e) => setAppType(e.target.value)}
//             />
//           </div>
//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">App Secret</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={appSecret}
//               onChange={(e) => setAppSecret(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Redirect URL</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={redirectUrl}
//               onChange={(e) => setRedirectUrl(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">App Id Type</Label>
//             <Input
//               className="text-white"
//               type="text"
//               value={appIdType}
//               onChange={(e) => setAppIdType(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Status</Label>
//             <Select value={status} onValueChange={(val) => setStatus(val)}>
//               <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
//                 <SelectValue placeholder="Select Status" />
//               </SelectTrigger>
//               <SelectContent className="border-zinc-700">
//                 <SelectGroup>
//                   {statusOptions.map((s) => (
//                     <SelectItem key={s.value} value={s.value}>
//                       {s.label}
//                     </SelectItem>
//                   ))}
//                 </SelectGroup>
//               </SelectContent>
//             </Select>
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Phone Number</Label>
//             <Input
//               className="text-white"
//               type="number"
//               value={phoneNumber}
//               onChange={(e) => setPhoneNumber(e.target.value)}
//             />
//           </div>

//           <div className="grid grid-cols-1 items-center gap-4">
//             <Label className="text-white">Muliplier Number</Label>
//             <Input
//               className="text-white"
//               type="number"
//               value={multiplier}
//               onChange={(e) => setMultiplier(e.target.value)}
//             />
//           </div>
//         </div>

//         {activationUrl && (
//           <div className="mt-4 p-3 bg-zinc-900 rounded border border-zinc-700">
//             <Label className="text-white block mb-2">Activation URL:</Label>
//             <div className="bg-zinc-800 p-2 rounded text-green-400 overflow-auto break-all">
//               {activationUrl}
//             </div>
//           </div>
//         )}

//         <DialogFooter className="mt-6 flex justify-between">
//           <Button
//             type="button"
//             className="text-[16px]  bg-[#07F2C7]  text-black font-medium hover:bg-[#07F2C7]/80 "
//             onClick={handleActivateUser}
//           >
//             Activate User
//           </Button>
//           <Button
//             type="submit"
//             className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
//             onClick={handleAddNewUser}
//           >
//             Add New User
//           </Button>
//         </DialogFooter>
//       </DialogContent>
//     </Dialog>
//   );
// }
